import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown, ArrowUp, Boxes, ChevronDown, Image as ImageIcon, Info, Layers, Lock, Palette, Plus, RotateCcw,
  Ruler, SlidersHorizontal, Sparkles, Trash2, Type, X,
} from 'lucide-react';

import { loadProductOptions, saveProductOptions, updateProductVariant } from './api';
import { Segmented, XomicsSelect } from './parts';
import { Field, errorMessage, fieldAria } from './ui';
import { tx } from './i18n';

// A product's options (Modules/ProductOptions.js on the backend): questions a
// shopper answers before buying, a choice of values or a short text. The
// backend's rules are checked here first, so a mistake is named beside the
// option instead of coming back as a refused save.
const OPTION_CODE_RE = /^[a-z][a-z0-9-]{0,31}$/;
const VALUE_CODE_RE = /^[a-z0-9][a-z0-9-]{0,31}$/;
const MAX_OPTIONS = 12;
const MAX_VALUES = 64;
const MAX_VARIANTS = 2000;
const MAX_ATTRIBUTES_BYTES = 2048;
const MAX_VARIANT_ROWS = 300;
// Attributes with their own fields here; storefronts read them to draw a value.
// Anything else a value carries (a ring's tones) is kept as it is, never shown
// as something to edit by hand.
const APPEARANCE_KEYS = ['swatch', 'texture', 'material', 'group', 'groupName'];
// Textures the swatches know how to draw (productDetail.css and the storefront's
// pickers). A texture outside this list is kept and drawn flat.
const TEXTURES = ['polished', 'brushed', 'matte', 'glaze', 'perforated', 'woven'];
const TEXTURE_LABEL = {
  polished: 'ui.products.options.texture_polished',
  brushed: 'ui.products.options.texture_brushed',
  matte: 'ui.products.options.texture_matte',
  glaze: 'ui.products.options.texture_glaze',
  perforated: 'ui.products.options.texture_perforated',
  woven: 'ui.products.options.texture_woven',
};

// Starting points for a new option. Codes are identifiers the storefronts and
// orders refer to, so they stay English whatever the portal's language.
const PRESETS = [
  { code: 'colour', Icon: Palette, look: true, label: 'ui.products.options.preset_colour', detail: 'ui.products.options.preset_colour_detail' },
  { code: 'size', Icon: Ruler, label: 'ui.products.options.preset_size', detail: 'ui.products.options.preset_size_detail' },
  { code: 'finish', Icon: Sparkles, look: true, label: 'ui.products.options.preset_finish', detail: 'ui.products.options.preset_finish_detail' },
  { code: 'material', Icon: Layers, look: true, label: 'ui.products.options.preset_material', detail: 'ui.products.options.preset_material_detail' },
  {
    code: 'engraving', Icon: Type, kind: 'text', maxLength: 20, required: false,
    label: 'ui.products.options.preset_engraving', detail: 'ui.products.options.preset_engraving_detail',
  },
  { code: 'option', Icon: SlidersHorizontal, custom: true, label: 'ui.products.options.preset_custom', detail: 'ui.products.options.preset_custom_detail' },
];

let lastKey = 0;
function nextKey() {
  lastKey += 1;
  return `d${lastKey}`;
}

export function slug(text) {
  return String(text ?? '').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+/, '').slice(0, 32).replace(/-+$/, '');
}

export function uniqueCode(base, taken, { letterFirst = false, fallback }) {
  let root = base || fallback;
  if (letterFirst && !/^[a-z]/.test(root)) root = `${fallback}-${root}`.slice(0, 32).replace(/-+$/, '');
  let code = root;
  for (let n = 2; taken.has(code); n += 1) {
    const suffix = `-${n}`;
    code = `${root.slice(0, 32 - suffix.length).replace(/-+$/, '')}${suffix}`;
  }
  return code;
}

function minorUnitOf(currencies, code) {
  return Number(currencies.find((currency) => currency.code === code)?.minorUnit ?? 2);
}

function toMinor(text, unit) {
  return Math.round(Number(text) * (10 ** unit));
}

function formatMoney(minor, currency, unit) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(minor / (10 ** unit));
  } catch (_error) {
    return `${currency} ${(minor / (10 ** unit)).toFixed(unit)}`;
  }
}

// ---------------------------------------------------------------- the draft

function valueDraft(value, saved, currencies) {
  const attributes = value?.attributes && typeof value.attributes === 'object' ? value.attributes : {};
  const extra = Object.fromEntries(Object.entries(attributes).filter(([key]) => !APPEARANCE_KEYS.includes(key)));
  const text = (key) => (typeof attributes[key] === 'string' ? attributes[key] : '');
  return {
    key: nextKey(),
    saved,
    code: value?.code ?? '',
    codeEdited: saved,
    name: value?.name ?? '',
    group: text('group'),
    groupName: text('groupName'),
    swatch: text('swatch'),
    texture: text('texture'),
    material: text('material'),
    extra: Object.keys(extra).length === 0 ? '' : JSON.stringify(extra, null, 2),
    prices: Object.fromEntries(Object.entries(value?.prices ?? {}).map(([currency, minor]) => (
      [currency, String(Number(minor) / (10 ** minorUnitOf(currencies, currency)))]
    ))),
  };
}

function hasLook(value) {
  return value.swatch.trim() !== '' || value.texture.trim() !== '' || /"tones"/.test(value.extra);
}

// The options as the editor holds them, from the document GET /Products/:id/options
// answers. Switched-off options are left out; a live option's switched-off
// values are kept aside so they can be brought back.
export function draftFrom(document, currencies) {
  return (document?.options ?? []).filter((option) => option.isActive !== false).map((option) => {
    const values = (option.values ?? []).filter((value) => value.isActive !== false)
      .map((value) => valueDraft(value, true, currencies));
    return {
      key: nextKey(),
      saved: true,
      code: option.code,
      codeEdited: true,
      name: option.name,
      kind: option.kind,
      required: option.required,
      maxLength: option.maxLength === null || option.maxLength === undefined ? '' : String(option.maxLength),
      look: values.some(hasLook),
      values,
      removed: (option.values ?? []).filter((value) => value.isActive === false)
        .map((value) => valueDraft(value, true, currencies)),
    };
  });
}

function fingerprint(options) {
  return JSON.stringify(options.map(({
    key, codeEdited, removed, look, ...option
  }) => ({
    ...option,
    values: option.values.map(({ key: valueKey, codeEdited: valueEdited, ...value }) => value),
  })));
}

// The value's attributes as the backend stores them; undefined when More
// attributes is not a JSON object.
function attributesOf(value) {
  let extra = {};
  if (value.extra.trim() !== '') {
    try {
      extra = JSON.parse(value.extra);
    } catch (_error) {
      return undefined;
    }
    if (extra === null || typeof extra !== 'object' || Array.isArray(extra)) return undefined;
  }
  const attributes = { ...extra };
  APPEARANCE_KEYS.forEach((key) => { delete attributes[key]; });
  if (value.groupName.trim() !== '') {
    attributes.group = value.group || slug(value.groupName);
    attributes.groupName = value.groupName.trim();
  } else if (value.group !== '') {
    attributes.group = value.group;
  }
  if (value.swatch.trim() !== '') attributes.swatch = value.swatch.trim();
  if (value.texture.trim() !== '') attributes.texture = value.texture.trim().toLowerCase();
  if (value.material.trim() !== '') attributes.material = value.material.trim();
  return Object.keys(attributes).length === 0 ? null : attributes;
}

function pricesOf(value, currencies) {
  const prices = {};
  Object.entries(value.prices).forEach(([currency, text]) => {
    if (String(text).trim() === '') return;
    const minor = toMinor(text, minorUnitOf(currencies, currency));
    if (minor > 0) prices[currency] = minor;
  });
  return prices;
}

function optionsDocument(options, currencies) {
  return options.map((option) => {
    const base = { code: option.code, name: option.name.trim(), kind: option.kind };
    // Only a text can be left empty; a choice is always made.
    if (option.kind === 'text') {
      return { ...base, required: option.required, maxLength: Number(option.maxLength), values: [] };
    }
    return {
      ...base,
      values: option.values.map((value) => ({
        code: value.code,
        name: value.name.trim(),
        attributes: attributesOf(value),
        prices: pricesOf(value, currencies),
      })),
    };
  });
}

function problemWith(options, currencies) {
  if (options.length > MAX_OPTIONS) return tx('ui.products.options.error_too_many_options', { max: MAX_OPTIONS });
  const codes = new Set();
  for (const option of options) {
    const name = option.name.trim();
    if (name === '') return tx('ui.products.options.error_option_name');
    if (!OPTION_CODE_RE.test(option.code)) return tx('ui.products.options.error_option_code', { option: name });
    if (codes.has(option.code)) return tx('ui.products.options.error_option_code_taken', { code: option.code });
    codes.add(option.code);
    if (option.kind === 'text') {
      const max = Number(option.maxLength);
      if (!Number.isSafeInteger(max) || max < 1 || max > 255) {
        return tx('ui.products.options.error_max_length', { option: name });
      }
      continue;
    }
    if (option.values.length === 0) return tx('ui.products.options.error_no_values', { option: name });
    if (option.values.length > MAX_VALUES) {
      return tx('ui.products.options.error_too_many_values', { option: name, max: MAX_VALUES });
    }
    const valueCodes = new Set();
    for (const value of option.values) {
      const label = value.name.trim() || value.code;
      if (value.name.trim() === '') return tx('ui.products.options.error_value_name', { option: name });
      if (!VALUE_CODE_RE.test(value.code)) return tx('ui.products.options.error_value_code', { value: label, option: name });
      if (valueCodes.has(value.code)) return tx('ui.products.options.error_value_code_taken', { code: value.code, option: name });
      valueCodes.add(value.code);
      const attributes = attributesOf(value);
      if (attributes === undefined) return tx('ui.products.options.attributes_invalid_for', { value: label });
      if (attributes !== null
          && new TextEncoder().encode(JSON.stringify(attributes)).length > MAX_ATTRIBUTES_BYTES) {
        return tx('ui.products.options.error_attributes_size', { value: label });
      }
      for (const [currency, text] of Object.entries(value.prices)) {
        if (String(text).trim() === '') continue;
        const minor = toMinor(text, minorUnitOf(currencies, currency));
        if (!Number.isSafeInteger(minor) || minor < 0) {
          return tx('ui.products.options.error_price', { value: label, currency });
        }
      }
    }
  }
  const combinations = options.filter((option) => option.kind === 'choice')
    .reduce((count, option) => count * option.values.length, 1);
  if (combinations > MAX_VARIANTS) return tx('ui.products.options.error_too_many_variants', { max: MAX_VARIANTS });
  return null;
}

// ---------------------------------------------------------------- variants

function selectionKey(selection) {
  return Object.keys(selection).sort().map((code) => `${code}=${selection[code]}`).join(';');
}

// Every combination of the product's choices (each is kept in stock by
// itself), beside the variant that already holds it (its SKU) or the SKU
// saving will give it, named the way the backend names the ones it makes.
function variantRows(options, variants, productCode) {
  const stockOptions = options.filter((option) => option.kind === 'choice' && option.values.length > 0);
  if (stockOptions.length === 0) return [];
  let combinations = [{}];
  stockOptions.forEach((option) => {
    combinations = combinations.flatMap((combination) => option.values.map((value) => (
      { ...combination, [option.code]: value.code }
    )));
  });
  if (combinations.length > MAX_VARIANTS) return [];
  const existingByKey = new Map((variants ?? []).map((variant) => [selectionKey(variant.selection), variant]));
  return combinations.map((selection) => {
    const key = selectionKey(selection);
    const existing = existingByKey.get(key) ?? null;
    return {
      key,
      selection,
      existing,
      sku: existing?.sku ?? [productCode, ...stockOptions.map((option) => selection[option.code])]
        .join('-').toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 64),
      labels: stockOptions.map((option) => (
        option.values.find((value) => value.code === selection[option.code])?.name ?? selection[option.code]
      )),
    };
  });
}

// What the Overview says about a product's options.
export function optionsSummary(document) {
  const options = (document?.options ?? []).filter((option) => option.isActive !== false);
  const choices = options.filter((option) => option.kind === 'choice');
  // The variants of the product's choices as they are now, not of a finish that was removed.
  const variants = (document?.variants ?? []).filter((variant) => variant.active && variant.current !== false);
  return {
    count: options.length,
    names: options.map((option) => option.name),
    combinations: choices.length === 0 ? 0 : choices.reduce((count, option) => (
      count * option.values.filter((value) => value.isActive !== false).length
    ), 1),
    requiresChoice: options.some((option) => option.required),
    variantStock: variants.reduce((sum, variant) => sum + (variant.stock ?? 0), 0),
    variantCount: variants.length,
  };
}

// ---------------------------------------------------------------- pieces

export function Switch({ on, onChange, label, disabled = false }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
      className={`xo-pm-switch${on ? ' is-on' : ''}`} onClick={() => onChange(!on)}>
      <i aria-hidden="true" />
    </button>
  );
}

function toneStyle(value) {
  let tones = null;
  if (value.extra.trim() !== '') {
    try {
      tones = JSON.parse(value.extra)?.tones ?? null;
    } catch (_error) {
      tones = null;
    }
  }
  const tone = (name) => (tones && typeof tones[name] === 'string' ? tones[name] : '');
  const body = tone('body') || value.swatch.trim();
  if (body === '') return undefined;
  return {
    '--sw': body,
    '--sw-hi': tone('hi') || body,
    '--sw-sh': tone('sh') || body,
    '--sw-rim': tone('rim') || body,
  };
}

// A value's swatch, drawn from its colour (or tones) and texture the way the
// storefront's pickers draw them.
function Swatch({ value }) {
  const style = toneStyle(value);
  const texture = slug(value.texture);
  return <span aria-hidden="true" style={style}
    className={`xo-pm-swatch${texture ? ` is-${texture}` : ''}${style ? '' : ' is-empty'}`} />;
}

// A swatch from stored attributes, for places that read values as XOmics
// stores them (the Images tab's choices).
export function AttributeSwatch({ attributes }) {
  const text = (key) => (typeof attributes?.[key] === 'string' ? attributes[key] : '');
  return <Swatch value={{
    swatch: text('swatch'),
    texture: text('texture'),
    extra: attributes?.tones ? JSON.stringify({ tones: attributes.tones }) : '',
  }} />;
}

function valueLabel(value) {
  const group = value.groupName.trim();
  return group ? `${group} ${value.name}` : value.name;
}

function linksTo(image, optionCode, valueCode) {
  return (image.optionValues ?? []).some((link) => link.option === optionCode && link.value === valueCode);
}

// The photo a shopper sees for what they picked: the first photo linked to
// their choices (every linked option matching), else the main photo.
function photoFor(images, picks) {
  const matches = (image) => {
    const links = image.optionValues ?? [];
    const byOption = new Map();
    links.forEach((link) => byOption.set(link.option, [...(byOption.get(link.option) ?? []), link.value]));
    return [...byOption].every(([option, values]) => values.includes(picks[option]));
  };
  const linked = images.find((image) => (image.optionValues ?? []).length > 0 && matches(image));
  const shared = images.filter((image) => (image.optionValues ?? []).length === 0);
  return linked ?? shared.find((image) => image.isMain) ?? shared[0] ?? images.find((image) => image.isMain) ?? null;
}

function priceSummary(value, currencies) {
  const set = Object.entries(value.prices).filter(([, text]) => String(text).trim() !== '' && Number(text) > 0);
  if (set.length === 0) return '';
  const [currency, text] = set.find(([code]) => code === 'EUR') ?? set[0];
  const unit = minorUnitOf(currencies, currency);
  return `+${formatMoney(toMinor(text, unit), currency, unit)}`;
}

// Names from one line: "Gold, Silver", or a run of sizes such as "6-13".
function parseValueNames(text) {
  const names = [];
  String(text).split(/[,;\n]+/).forEach((part) => {
    const token = part.trim();
    if (token === '') return;
    const run = token.match(/^(\d{1,3})\s*(?:-|–|\.\.|to)\s*(\d{1,3})$/i);
    if (run && Number(run[2]) >= Number(run[1]) && Number(run[2]) - Number(run[1]) < MAX_VALUES) {
      for (let n = Number(run[1]); n <= Number(run[2]); n += 1) names.push(String(n));
      return;
    }
    names.push(token.slice(0, 64));
  });
  return names;
}

function QuickAdd({ onAdd, disabled }) {
  const [text, setText] = useState('');
  function add() {
    const names = parseValueNames(text);
    if (names.length === 0) return;
    onAdd(names);
    setText('');
  }
  return (
    <div className="xo-pm-quick-add">
      <Plus aria-hidden="true" />
      <input value={text} disabled={disabled} aria-label={tx('ui.products.options.add_values')}
        placeholder={tx('ui.products.options.add_values_placeholder')}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); add(); } }} />
      <button type="button" className="xo-btn-2" disabled={disabled || text.trim() === ''} onClick={add}>
        {tx('ui.products.options.add')}
      </button>
    </div>
  );
}

function PresetList({ onPick, compact = false }) {
  return (
    <div className={`xo-pm-presets${compact ? ' is-compact' : ''}`}>
      {PRESETS.map((preset) => {
        const Icon = preset.Icon;
        return (
          <button type="button" key={preset.code} role={compact ? 'menuitem' : undefined} onClick={() => onPick(preset)}>
            <span className="xo-pm-preset-icon"><Icon aria-hidden="true" /></span>
            <span><strong>{tx(preset.label)}</strong><small>{tx(preset.detail)}</small></span>
          </button>
        );
      })}
    </div>
  );
}

function AddOptionMenu({ onPick, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const closeOnPointer = (event) => { if (!ref.current?.contains(event.target)) setOpen(false); };
    const closeOnEscape = (event) => { if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', closeOnPointer);
    document.addEventListener('keydown', closeOnEscape, true);
    return () => {
      document.removeEventListener('mousedown', closeOnPointer);
      document.removeEventListener('keydown', closeOnEscape, true);
    };
  }, [open]);
  return (
    <div className="xo-pm-add-option" ref={ref}>
      <button type="button" className="xo-btn-2" aria-haspopup="menu" aria-expanded={open} disabled={disabled}
        onClick={() => setOpen((current) => !current)}>
        <Plus aria-hidden="true" />{tx('ui.products.options.add_option')}
      </button>
      {open && (
        <div className="xo-pm-add-option-menu" role="menu">
          <PresetList compact onPick={(preset) => { setOpen(false); onPick(preset); }} />
        </div>
      )}
    </div>
  );
}

const NEW_CHOICE = '__new__';

// A dropdown of the names this option already uses, plus a way to add one: a
// name is typed only when it is genuinely new, then it is picked like the rest.
function PickOrAdd({
  id, label, hint, value, choices, noneLabel, newLabel, maxLength, onChange,
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const current = value.trim();
  const known = [...new Set([...choices, current].map((choice) => choice.trim()).filter(Boolean))];
  function commit() {
    const name = draft.trim();
    if (name === '') return;
    onChange(name);
    setAdding(false);
  }
  return (
    <Field id={id} label={label} hint={hint}>
      {adding ? (
        <span className="xo-pm-new-choice">
          <input {...fieldAria(id, { hint: Boolean(hint) })} autoFocus value={draft} maxLength={maxLength}
            placeholder={tx('ui.products.options.name_it')}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') { event.preventDefault(); commit(); }
              // Escape leaves the new name, not the whole product sheet.
              if (event.key === 'Escape') { event.nativeEvent.stopPropagation(); setAdding(false); }
            }} />
          <button type="button" className="xo-btn-2" disabled={draft.trim() === ''} onClick={commit}>
            {tx('ui.products.options.add')}
          </button>
          <button type="button" className="xo-icon-btn" aria-label={tx('ui.products.cancel_77dfd213')}
            onClick={() => setAdding(false)}><X aria-hidden="true" /></button>
        </span>
      ) : (
        <XomicsSelect {...fieldAria(id, { hint: Boolean(hint) })} value={current}
          onChange={(event) => {
            if (event.target.value === NEW_CHOICE) {
              setDraft('');
              setAdding(true);
              return;
            }
            onChange(event.target.value);
          }}>
          <option value="">{noneLabel}</option>
          {known.map((choice) => <option key={choice} value={choice}>{choice}</option>)}
          <option value={NEW_CHOICE}>{newLabel}</option>
        </XomicsSelect>
      )}
    </Field>
  );
}

function textureLabel(texture) {
  return TEXTURE_LABEL[texture] ? tx(TEXTURE_LABEL[texture]) : texture;
}

// A new colour replaces any tones the swatch was drawn from before, so the
// swatch (and every storefront) follows the colour chosen here.
function withoutTones(extra) {
  if (extra.trim() === '') return '';
  try {
    const { tones, ...rest } = JSON.parse(extra);
    return Object.keys(rest).length === 0 ? '' : JSON.stringify(rest, null, 2);
  } catch (_error) {
    return extra;
  }
}

function ValueEditor({
  value, position, count, groups, materials, currencies, drawn, photos, onManagePhotos, onChange, onMove, onRemove,
}) {
  const id = `pm-value-${value.key}`;
  const hasPrices = Object.values(value.prices).some((text) => String(text).trim() !== '');
  // Without a colour of its own, the picker starts from the tone the swatch is drawn in.
  const drawnColour = toneStyle(value)?.['--sw'] ?? '';
  const pickerColour = [value.swatch.trim(), drawnColour].find((colour) => /^#[0-9a-f]{6}$/i.test(colour)) ?? '#000000';
  const texture = value.texture.trim().toLowerCase();
  const textures = texture !== '' && !TEXTURES.includes(texture) ? [...TEXTURES, texture] : TEXTURES;
  const setColour = (colour) => onChange({ swatch: colour, extra: withoutTones(value.extra) });
  const appearance = (
    <div className="xo-pm-grid">
      <Field id={`${id}-swatch`} label={tx('ui.products.options.colour')}>
        <span className="xo-pm-colour-control">
          <input type="color" aria-label={tx('ui.products.options.pick_colour')} value={pickerColour}
            className={value.swatch.trim() === '' ? 'is-unset' : ''}
            onChange={(event) => setColour(event.target.value)} />
          <input {...fieldAria(`${id}-swatch`)} value={value.swatch} placeholder={drawnColour || '#313131'} maxLength={7}
            onChange={(event) => setColour(event.target.value)} />
        </span>
      </Field>
      <Field id={`${id}-texture`} label={tx('ui.products.options.texture')}>
        <XomicsSelect {...fieldAria(`${id}-texture`)} value={texture}
          onChange={(event) => onChange({ texture: event.target.value })}>
          <option value="">{tx('ui.products.options.none')}</option>
          {textures.map((entry) => <option key={entry} value={entry}>{textureLabel(entry)}</option>)}
        </XomicsSelect>
      </Field>
      <PickOrAdd id={`${id}-material`} label={tx('ui.products.options.material')} value={value.material}
        choices={materials} maxLength={64} noneLabel={tx('ui.products.options.none')}
        newLabel={tx('ui.products.options.new_material')} onChange={(material) => onChange({ material })} />
    </div>
  );
  return (
    <div className="xo-pm-value-editor">
      {photos && (
        <div className="xo-pm-value-photos">
          <span className="xo-pm-value-photos-strip">
            {photos.length === 0
              ? <small>{tx('ui.products.images.none_for', { value: valueLabel(value) })}</small>
              : photos.slice(0, 6).map((image) => (
                <img key={image.id} src={image.variants?.thumbnail?.url ?? image.url} alt="" loading="lazy" decoding="async" />
              ))}
            {photos.length > 6 && <small>+{photos.length - 6}</small>}
          </span>
          <button type="button" className="xo-link" onClick={onManagePhotos}>
            {photos.length === 0 ? tx('ui.products.images.add_photos') : tx('ui.products.images.manage')}
          </button>
        </div>
      )}
      <div className="xo-pm-grid is-two">
        <Field id={`${id}-name`} label={tx('ui.products.options.value_name')}>
          <input {...fieldAria(`${id}-name`)} value={value.name} maxLength={64}
            onChange={(event) => onChange({ name: event.target.value })} />
        </Field>
        <PickOrAdd id={`${id}-group`} label={tx('ui.products.options.group')} hint={tx('ui.products.options.group_hint')}
          value={value.groupName} choices={groups} maxLength={40} noneLabel={tx('ui.products.options.no_group')}
          newLabel={tx('ui.products.options.new_group')}
          onChange={(groupName) => onChange({ groupName, group: slug(groupName) })} />
      </div>
      {drawn ? appearance : (
        <details className="xo-pm-more">
          <summary><ChevronDown aria-hidden="true" />{tx('ui.products.options.appearance')}<small>{tx('ui.products.options.appearance_hint')}</small></summary>
          {appearance}
        </details>
      )}
      <details className="xo-pm-more" open={hasPrices || undefined}>
        <summary><ChevronDown aria-hidden="true" />{tx('ui.products.options.price_addon')}<small>{tx('ui.products.options.price_addon_hint')}</small></summary>
        <div className="xo-pm-price-grid">
          {currencies.filter((currency) => currency.isActive).map((currency) => (
            <label key={currency.code}>
              <span>{currency.code}</span>
              <input type="number" min="0" inputMode="decimal" placeholder="0"
                step={1 / (10 ** Number(currency.minorUnit ?? 2))} value={value.prices[currency.code] ?? ''}
                onChange={(event) => onChange({ prices: { ...value.prices, [currency.code]: event.target.value } })} />
            </label>
          ))}
        </div>
      </details>
      <div className="xo-pm-value-actions">
        <button type="button" className="xo-link" disabled={position === 0} onClick={() => onMove(-1)}>
          <ArrowUp aria-hidden="true" />{tx('ui.products.options.move_up')}
        </button>
        <button type="button" className="xo-link" disabled={position === count - 1} onClick={() => onMove(1)}>
          <ArrowDown aria-hidden="true" />{tx('ui.products.options.move_down')}
        </button>
        <button type="button" className="xo-link is-danger" onClick={onRemove}>
          <Trash2 aria-hidden="true" />{tx('ui.products.options.remove_value')}
        </button>
      </div>
    </div>
  );
}

function OptionCard({
  option, index, count, currencies, openValue, setOpenValue, disabled, images, photoOption, onManagePhotos,
  onChange, onMove, onRemove, onValueChange, onAddValues, onRemoveValue, onRestoreValue, onMoveValue,
}) {
  const drawn = option.look || option.values.some(hasLook);
  // A saved value of the option photos follow shows its photos in its editor.
  const photosOf = (value) => (option.code === photoOption && value.saved && onManagePhotos
    ? images.filter((image) => linksTo(image, option.code, value.code)) : null);
  const Icon = option.kind === 'text' ? Type : drawn ? Palette : Boxes;
  const groups = [...new Set(option.values.map((value) => value.groupName.trim()).filter(Boolean))];
  const materials = [...new Set(option.values.map((value) => value.material.trim()).filter(Boolean))];
  const summary = [
    option.kind === 'text'
      ? tx('ui.products.options.text_up_to', { count: option.maxLength || '?' })
      : option.values.length === 1
        ? tx('ui.products.options.value_count_one')
        : tx('ui.products.options.value_count', { count: option.values.length }),
    // A choice is always made; only a text can be left empty.
    ...(option.kind === 'text'
      ? [option.required ? tx('ui.products.options.required_short') : tx('ui.products.options.optional_short')] : []),
  ].join(' · ');
  return (
    <article className="xo-pm-option">
      <header className="xo-pm-option-head">
        <span className="xo-pm-option-icon"><Icon aria-hidden="true" /></span>
        <div className="xo-pm-option-title">
          <input className="xo-pm-option-name" value={option.name} maxLength={64} disabled={disabled}
            autoFocus={!option.saved && option.name === ''}
            placeholder={tx('ui.products.options.option_name')} aria-label={tx('ui.products.options.option_name')}
            onChange={(event) => onChange({ name: event.target.value })} />
          <span className="xo-pm-option-meta">
            <span className="xo-pm-code" title={option.saved ? tx('ui.products.options.code_fixed') : undefined}>
              {option.saved && <Lock aria-hidden="true" />}{option.code}
            </span>
            <span>{summary}</span>
          </span>
        </div>
        <div className="xo-pm-option-tools">
          <button type="button" className="xo-icon-btn" disabled={disabled || index === 0}
            aria-label={tx('ui.products.options.move_up')} onClick={() => onMove(-1)}><ArrowUp aria-hidden="true" /></button>
          <button type="button" className="xo-icon-btn" disabled={disabled || index === count - 1}
            aria-label={tx('ui.products.options.move_down')} onClick={() => onMove(1)}><ArrowDown aria-hidden="true" /></button>
          <button type="button" className="xo-icon-btn is-danger" disabled={disabled}
            aria-label={tx('ui.products.options.remove_option', { option: option.name || option.code })}
            onClick={onRemove}><Trash2 aria-hidden="true" /></button>
        </div>
      </header>

      <div className="xo-pm-option-settings">
        <Segmented value={option.kind}
          options={[
            { value: 'choice', label: tx('ui.products.options.kind_choice') },
            { value: 'text', label: tx('ui.products.options.kind_text') },
          ]}
          onChange={(kind) => onChange({
            kind,
            required: kind === 'choice' ? true : option.required,
            maxLength: kind === 'text' && option.maxLength === '' ? '20' : option.maxLength,
          })} />
        {/* A choice is always made, and each of its values is kept in stock by itself. */}
        {option.kind === 'text' && (
          <>
            <label className="xo-pm-check">
              <input type="checkbox" checked={option.required} disabled={disabled}
                onChange={(event) => onChange({ required: event.target.checked })} />
              <span>{tx('ui.products.options.required')}</span>
            </label>
            <label className="xo-pm-inline-number">
              <span>{tx('ui.products.options.max_length')}</span>
              <input type="number" min="1" max="255" value={option.maxLength} disabled={disabled}
                onChange={(event) => onChange({ maxLength: event.target.value })} />
            </label>
          </>
        )}
      </div>

      {option.kind === 'choice' ? (
        <div className="xo-pm-values">
          {drawn ? option.values.map((value, position) => {
            // Values with a look are rows with their swatch, opening in place.
            const open = openValue === value.key;
            const group = value.groupName.trim();
            const startsGroup = group !== '' && group !== (option.values[position - 1]?.groupName.trim() ?? '');
            return (
              <React.Fragment key={value.key}>
                {startsGroup && <p className="xo-pm-value-group">{group}</p>}
                <div className={`xo-pm-value${open ? ' is-open' : ''}`}>
                  <button type="button" className="xo-pm-value-row" aria-expanded={open} disabled={disabled}
                    onClick={() => setOpenValue(open ? null : value.key)}>
                    <Swatch value={value} />
                    <span className="xo-pm-value-name">{value.name || tx('ui.products.options.untitled_value')}</span>
                    <code>{value.code}</code>
                    <span className="xo-pm-value-price">{priceSummary(value, currencies)}</span>
                    <ChevronDown aria-hidden="true" />
                  </button>
                  {open && (
                    <ValueEditor value={value} position={position} count={option.values.length} groups={groups}
                      materials={materials} currencies={currencies} drawn
                      photos={photosOf(value)} onManagePhotos={() => onManagePhotos(value.code)}
                      onChange={(patch) => onValueChange(value.key, patch)}
                      onMove={(step) => onMoveValue(value.key, step)}
                      onRemove={() => onRemoveValue(value.key)} />
                  )}
                </div>
              </React.Fragment>
            );
          }) : (
            // Plain values (sizes, lengths) are chips; the one opened edits below them.
            <>
              <div className="xo-pm-value-chips">
                {option.values.map((value, position) => {
                  const open = openValue === value.key;
                  const group = value.groupName.trim();
                  const startsGroup = group !== '' && group !== (option.values[position - 1]?.groupName.trim() ?? '');
                  const price = priceSummary(value, currencies);
                  return (
                    <React.Fragment key={value.key}>
                      {startsGroup && <p className="xo-pm-value-group">{group}</p>}
                      <button type="button" className={`xo-pm-value-chip${open ? ' is-open' : ''}`} aria-expanded={open}
                        disabled={disabled} onClick={() => setOpenValue(open ? null : value.key)}>
                        <span>{value.name || tx('ui.products.options.untitled_value')}</span>
                        {price && <small>{price}</small>}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
              {option.values.map((value, position) => (openValue === value.key ? (
                <div key={value.key} className="xo-pm-value is-open">
                  <ValueEditor value={value} position={position} count={option.values.length} groups={groups}
                    materials={materials} currencies={currencies} drawn={false}
                    photos={photosOf(value)} onManagePhotos={() => onManagePhotos(value.code)}
                    onChange={(patch) => onValueChange(value.key, patch)}
                    onMove={(step) => onMoveValue(value.key, step)}
                    onRemove={() => onRemoveValue(value.key)} />
                </div>
              ) : null))}
            </>
          )}
          <QuickAdd onAdd={onAddValues} disabled={disabled} />
          {option.removed.length > 0 && (
            <div className="xo-pm-removed">
              <span>{tx('ui.products.options.removed_values')}</span>
              {option.removed.map((value) => (
                <button type="button" key={value.key} disabled={disabled} onClick={() => onRestoreValue(value.key)}
                  title={tx('ui.products.options.restore_value', { value: value.name })}>
                  <RotateCcw aria-hidden="true" />{value.name}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="xo-pm-hint">{tx('ui.products.options.text_hint')}</p>
      )}
    </article>
  );
}

// A combination as the catalogue has it: what it is and whether it is sold at
// all. How many there are of it is each office's own, in its store.
function VariantRow({ productId, row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function change(changes) {
    setBusy(true);
    setError('');
    try {
      await updateProductVariant(productId, row.existing.sku, changes);
      onChanged(row.existing.sku, changes);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div role="row" className={`xo-pm-variant${row.existing ? '' : ' is-new'}${row.existing && !row.existing.active ? ' is-off' : ''}`}>
      <span role="cell" className="xo-pm-variant-combo">{row.labels.map((label, index) => <b key={`${label}-${index}`}>{label}</b>)}</span>
      <span role="cell" className="xo-pm-variant-sku"><code>{row.sku}</code></span>
      <span role="cell" className="xo-pm-variant-stock">
        {!row.existing && <small>{tx('ui.products.options.created_on_save')}</small>}
      </span>
      <span role="cell" className="xo-pm-variant-state">
        {row.existing && (
          <Switch on={row.existing.active} disabled={busy}
            label={tx('ui.products.options.sold_of', { sku: row.sku })}
            onChange={(active) => change({ active })} />
        )}
      </span>
      {error && <p className="xo-pm-variant-error" role="alert">{error}</p>}
    </div>
  );
}

function VariantTable({ productId, rows, onVariantChanged }) {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const shown = rows.filter((row) => needle === '' || `${row.sku} ${row.labels.join(' ')}`.toLowerCase().includes(needle));
  return (
    <section className="xo-pm-variants">
      <header>
        <div>
          <h4>{tx('ui.products.options.combinations')}</h4>
          <p>{tx('ui.products.options.combination_count', { count: rows.length.toLocaleString() })}</p>
        </div>
        {rows.length > 8 && (
          <input type="search" className="xo-pm-variant-search" value={query}
            placeholder={tx('ui.products.options.find_combination')} aria-label={tx('ui.products.options.find_combination')}
            onChange={(event) => setQuery(event.target.value)} />
        )}
      </header>
      <div className="xo-pm-variant-table" role="table" aria-label={tx('ui.products.options.combinations')}>
        <div role="row" className="xo-pm-variant is-head">
          <span role="columnheader">{tx('ui.products.options.combination')}</span>
          <span role="columnheader">{tx('ui.products.sku_d59192b9')}</span>
          <span role="columnheader" />
          <span role="columnheader">{tx('ui.products.options.sold')}</span>
        </div>
        {shown.slice(0, MAX_VARIANT_ROWS).map((row) => (
          <VariantRow key={`${row.key}:${row.existing ? 'made' : 'new'}`} productId={productId} row={row}
            onChanged={onVariantChanged} />
        ))}
      </div>
      {shown.length > MAX_VARIANT_ROWS && (
        <p className="xo-pm-hint">{tx('ui.products.options.more_combinations', { count: shown.length - MAX_VARIANT_ROWS })}</p>
      )}
    </section>
  );
}

function PreviewOption({ option, picked, onPick }) {
  const chosen = option.kind === 'choice' ? option.values.find((value) => value.key === picked) ?? null : null;
  const drawn = option.kind === 'choice' && (option.look || option.values.some(hasLook));
  return (
    <div className="xo-pm-preview-option">
      <p>
        <b>{option.name || tx('ui.products.options.untitled')}</b>
        {chosen && <span>{valueLabel(chosen)}</span>}
        {!chosen && !option.required && <span>{tx('ui.products.options.optional_short')}</span>}
      </p>
      {option.kind === 'text' && (
        <input type="text" value={picked ?? ''} maxLength={Number(option.maxLength) || undefined}
          aria-label={option.name} placeholder={tx('ui.products.options.text_up_to', { count: option.maxLength || '?' })}
          onChange={(event) => onPick(event.target.value)} />
      )}
      {drawn && (
        <div className="xo-pm-preview-swatches">
          {option.values.map((value, position) => {
            const group = value.groupName.trim();
            const startsGroup = group !== '' && group !== (option.values[position - 1]?.groupName.trim() ?? '');
            return (
              <React.Fragment key={value.key}>
                {startsGroup && <span className="xo-pm-preview-group">{group}</span>}
                <button type="button" className={value.key === picked ? 'is-picked' : ''} aria-pressed={value.key === picked}
                  title={valueLabel(value)} aria-label={valueLabel(value)} onClick={() => onPick(value.key)}>
                  <Swatch value={value} />
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
      {option.kind === 'choice' && !drawn && (
        <div className="xo-pm-preview-chips">
          {option.values.map((value) => (
            <button type="button" key={value.key} className={value.key === picked ? 'is-picked' : ''}
              aria-pressed={value.key === picked} onClick={() => onPick(value.key)}>{value.name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// The buy column as a shopper meets it, drawn from the unsaved draft: pick
// values to see the label an order keeps, the price with its add-ons and,
// when combinations track stock, the SKU the line would draw from.
function ShopperPreview({ product, options, rows, currencies }) {
  const priced = currencies.filter((currency) => (
    currency.isActive && product.prices.some((price) => price.currency === currency.code && price.isActive)
  ));
  const [currencyCode, setCurrencyCode] = useState(() => (
    (priced.find((currency) => currency.code === 'EUR') ?? priced[0])?.code ?? ''
  ));
  const [picked, setPicked] = useState({});
  const currency = priced.find((entry) => entry.code === currencyCode) ?? priced[0] ?? null;
  const unit = Number(currency?.minorUnit ?? 2);
  const base = currency
    ? product.prices.find((price) => price.currency === currency.code && price.isActive) ?? null : null;

  let extra = 0;
  const answers = [];
  const missing = [];
  options.forEach((option) => {
    const answer = picked[option.key];
    if (option.kind === 'text') {
      const text = String(answer ?? '').trim();
      if (text !== '') answers.push(`${option.name}: ${text}`);
      else if (option.required) missing.push(option.name || tx('ui.products.options.untitled'));
      return;
    }
    const value = option.values.find((entry) => entry.key === answer);
    if (!value) {
      if (option.required) missing.push(option.name || tx('ui.products.options.untitled'));
      return;
    }
    const delta = String(value.prices[currency?.code] ?? '').trim();
    if (delta !== '' && Number(delta) > 0) extra += toMinor(delta, unit);
    answers.push(`${option.name}: ${valueLabel(value)}`);
  });
  const chosenRow = missing.length > 0 ? null : rows.find((row) => Object.entries(row.selection).every(([code, valueCode]) => {
    const option = options.find((entry) => entry.code === code);
    return option?.values.find((value) => value.key === picked[option.key])?.code === valueCode;
  })) ?? null;
  const picks = {};
  options.forEach((option) => {
    const value = option.kind === 'choice' ? option.values.find((entry) => entry.key === picked[option.key]) : null;
    if (value) picks[option.code] = value.code;
  });
  const photo = photoFor(product.images, picks);
  const photoSrc = photo ? photo.variants?.card?.url ?? photo.variants?.detail?.url ?? photo.url : null;
  // How many there are is each office's own, so the catalogue's preview only
  // knows a combination that is not sold at all.
  const soldOut = rows.length > 0 && chosenRow !== null && (!chosenRow.existing || !chosenRow.existing.active);

  return (
    <aside className="xo-pm-preview" aria-label={tx('ui.products.options.preview')}>
      <div className="xo-pm-preview-head">
        <span>{tx('ui.products.options.preview')}</span>
        {priced.length > 1 && (
          <XomicsSelect className="xo-pm-preview-currency" aria-label={tx('ui.products.settings.price_currency')}
            value={currency?.code ?? ''} onChange={(event) => setCurrencyCode(event.target.value)}>
            {priced.map((entry) => <option key={entry.code} value={entry.code}>{entry.code}</option>)}
          </XomicsSelect>
        )}
      </div>
      <div className="xo-pm-preview-card">
        <div className="xo-pm-preview-photo">
          {photoSrc ? <img src={photoSrc} alt="" /> : <ImageIcon aria-hidden="true" />}
        </div>
        <p className="xo-pm-preview-name">{product.name}</p>
        <p className="xo-pm-preview-price">
          {base ? formatMoney(base.amountMinor + extra, base.currency, unit) : tx('ui.products.no_price')}
        </p>
        {base && extra > 0 && (
          <p className="xo-pm-preview-extra">{tx('ui.products.options.includes_extra', { amount: formatMoney(extra, base.currency, unit) })}</p>
        )}
        {options.length === 0 && <p className="xo-pm-preview-empty">{tx('ui.products.options.preview_empty')}</p>}
        {options.map((option) => (
          <PreviewOption key={option.key} option={option} picked={picked[option.key]}
            onPick={(answer) => setPicked((current) => ({ ...current, [option.key]: answer }))} />
        ))}
        <div className={`xo-pm-preview-buy${missing.length === 0 && !soldOut ? ' is-ready' : ''}`} aria-hidden="true">
          {soldOut ? tx('ui.products.options.out_of_stock') : tx('ui.products.options.add_to_bag')}
        </div>
        <p className="xo-pm-preview-status">
          {missing.length > 0
            ? tx('ui.products.options.choose_first', { names: missing.join(', ') })
            : answers.join(', ') || tx('ui.products.options.ready_to_buy')}
        </p>
        {chosenRow && (
          <p className="xo-pm-preview-sku">
            {chosenRow.existing
              ? chosenRow.existing.sku
              : tx('ui.products.options.sku_on_save', { sku: chosenRow.sku })}
          </p>
        )}
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------- editor

export default function ProductOptionsEditor({
  product, currencies, document, mayManage, onSaved, onDirtyChange, photoOption = null, onManagePhotos,
}) {
  const initial = useMemo(() => draftFrom(document, currencies), [document, currencies]);
  const [options, setOptions] = useState(initial);
  const [baseline] = useState(() => fingerprint(initial));
  const [variants, setVariants] = useState(document?.variants ?? []);
  const [openValue, setOpenValue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dirty = fingerprint(options) !== baseline;
  const rows = useMemo(() => variantRows(options, variants, product.code), [options, variants, product.code]);
  const requiresChoice = options.some((option) => option.required);
  const combinations = options.filter((option) => option.kind === 'choice' && option.values.length > 0)
    .reduce((count, option) => count * option.values.length, options.some((option) => option.kind === 'choice') ? 1 : 0);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  function edit(change) {
    setError('');
    setOptions(change);
  }

  function updateOption(key, patch) {
    edit((current) => current.map((option) => {
      if (option.key !== key) return option;
      const next = { ...option, ...patch };
      if (patch.name !== undefined && !option.saved && !option.codeEdited) {
        const taken = new Set(current.filter((entry) => entry.key !== key).map((entry) => entry.code));
        next.code = uniqueCode(slug(patch.name), taken, { letterFirst: true, fallback: 'option' });
      }
      return next;
    }));
  }

  function updateValues(optionKey, change) {
    edit((current) => current.map((option) => (option.key === optionKey ? change(option) : option)));
  }

  function updateValue(optionKey, valueKey, patch) {
    updateValues(optionKey, (option) => ({
      ...option,
      values: option.values.map((value) => {
        if (value.key !== valueKey) return value;
        const next = { ...value, ...patch };
        if (patch.name !== undefined && !value.saved && !value.codeEdited) {
          const taken = new Set([...option.values, ...option.removed]
            .filter((entry) => entry.key !== valueKey).map((entry) => entry.code));
          next.code = uniqueCode(slug(patch.name), taken, { fallback: 'value' });
        }
        return next;
      }),
    }));
  }

  function addValues(optionKey, names) {
    updateValues(optionKey, (option) => {
      const known = new Set(option.values.map((value) => value.name.trim().toLowerCase()));
      const taken = new Set([...option.values, ...option.removed].map((value) => value.code));
      const restored = new Set();
      const added = [];
      names.forEach((name) => {
        const lower = name.toLowerCase();
        if (known.has(lower)) return;
        known.add(lower);
        // A switched-off value of that name comes back as it was.
        const earlier = option.removed.find((value) => value.name.trim().toLowerCase() === lower);
        if (earlier) {
          restored.add(earlier.key);
          added.push(earlier);
          return;
        }
        const code = uniqueCode(slug(name), taken, { fallback: 'value' });
        taken.add(code);
        added.push({ ...valueDraft({ code, name }, false, currencies), codeEdited: false });
      });
      return {
        ...option,
        values: [...option.values, ...added],
        removed: option.removed.filter((value) => !restored.has(value.key)),
      };
    });
  }

  function restoreValue(optionKey, valueKey) {
    updateValues(optionKey, (option) => ({
      ...option,
      values: [...option.values, ...option.removed.filter((value) => value.key === valueKey)],
      removed: option.removed.filter((value) => value.key !== valueKey),
    }));
  }

  function removeValue(optionKey, valueKey) {
    setOpenValue(null);
    updateValues(optionKey, (option) => {
      const value = option.values.find((entry) => entry.key === valueKey);
      return {
        ...option,
        values: option.values.filter((entry) => entry.key !== valueKey),
        // Saved values are switched off by the save, so they can come back.
        removed: value?.saved ? [...option.removed, value] : option.removed,
      };
    });
  }

  function moveValue(optionKey, valueKey, step) {
    updateValues(optionKey, (option) => {
      const values = [...option.values];
      const from = values.findIndex((value) => value.key === valueKey);
      const to = from + step;
      if (from < 0 || to < 0 || to >= values.length) return option;
      [values[from], values[to]] = [values[to], values[from]];
      return { ...option, values };
    });
  }

  function addOption(preset) {
    edit((current) => {
      const taken = new Set(current.map((option) => option.code));
      return [...current, {
        key: nextKey(),
        saved: false,
        code: uniqueCode(preset.code, taken, { letterFirst: true, fallback: 'option' }),
        codeEdited: !preset.custom,
        name: preset.custom ? '' : tx(preset.label),
        kind: preset.kind ?? 'choice',
        required: preset.required ?? true,
        maxLength: preset.kind === 'text' ? String(preset.maxLength ?? 20) : '',
        look: Boolean(preset.look),
        values: [],
        removed: [],
      }];
    });
  }

  function moveOption(key, step) {
    edit((current) => {
      const next = [...current];
      const from = next.findIndex((option) => option.key === key);
      const to = from + step;
      if (from < 0 || to < 0 || to >= next.length) return current;
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
  }

  function removeOption(key) {
    edit((current) => current.filter((option) => option.key !== key));
  }

  function discard() {
    setOptions(draftFrom(document, currencies));
    setOpenValue(null);
    setError('');
  }

  async function save() {
    const problem = problemWith(options, currencies);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError('');
    try {
      // The combinations as they are now, read just before the save. (How many
      // there are of each is each office's own and is not part of this.)
      const current = await loadProductOptions(product.id);
      const payload = { options: optionsDocument(options, currencies) };
      const freshRows = variantRows(options, current.variants ?? [], product.code);
      if (freshRows.length > 0) {
        payload.variants = freshRows.map((row) => ({
          sku: row.sku,
          selection: row.selection,
          active: row.existing ? row.existing.active : true,
        }));
      }
      const saved = await saveProductOptions(product.id, payload);
      setBusy(false);
      onSaved(saved);
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  const disabled = !mayManage || busy;
  const summary = options.length === 0
    ? tx('ui.products.options.none_detail')
    : [
      options.length === 1
        ? tx('ui.products.options.option_count_one')
        : tx('ui.products.options.option_count', { count: options.length }),
      ...(combinations > 1 ? [tx('ui.products.options.combination_count', { count: combinations.toLocaleString() })] : []),
    ].join(' · ');

  return (
    <div className="xo-pm-options">
      <div className="xo-pm-options-main">
        <div className="xo-pm-section-head">
          <div>
            <h3>{tx('ui.products.options.title')}</h3>
            <p>{summary}</p>
          </div>
          {mayManage && options.length > 0 && <AddOptionMenu onPick={addOption} disabled={disabled} />}
        </div>

        {requiresChoice && (
          <p className="xo-pm-note"><Info aria-hidden="true" />{tx('ui.products.options.listed_where_supported')}</p>
        )}

        {options.length === 0 ? (
          <div className="xo-pm-options-empty">
            <strong>{tx('ui.products.options.none_title')}</strong>
            <p>{tx('ui.products.options.none_hint')}</p>
            {mayManage && <PresetList onPick={addOption} />}
          </div>
        ) : options.map((option, index) => (
          <OptionCard key={option.key} option={option} index={index} count={options.length}
            currencies={currencies} openValue={openValue} setOpenValue={setOpenValue} disabled={disabled}
            images={product.images} photoOption={photoOption} onManagePhotos={mayManage ? onManagePhotos : null}
            onChange={(patch) => updateOption(option.key, patch)}
            onMove={(step) => moveOption(option.key, step)}
            onRemove={() => removeOption(option.key)}
            onValueChange={(valueKey, patch) => updateValue(option.key, valueKey, patch)}
            onAddValues={(names) => addValues(option.key, names)}
            onRemoveValue={(valueKey) => removeValue(option.key, valueKey)}
            onRestoreValue={(valueKey) => restoreValue(option.key, valueKey)}
            onMoveValue={(valueKey, step) => moveValue(option.key, valueKey, step)} />
        ))}

        {rows.length > 0 && (
          <VariantTable productId={product.id} rows={rows}
            onVariantChanged={(sku, changes) => setVariants((current) => current.map((variant) => (
              variant.sku === sku ? { ...variant, ...changes } : variant
            )))} />
        )}
      </div>

      <ShopperPreview product={product} options={options} rows={rows} currencies={currencies} />

      {mayManage && (dirty || error) && (
        <div className="xo-pm-savebar">
          <span className={error ? 't-bad' : ''} role={error ? 'alert' : undefined}>
            {error || tx('ui.products.options.unsaved')}
          </span>
          <div>
            <button type="button" className="xo-btn-2" disabled={busy || !dirty} onClick={discard}>
              {tx('ui.products.detail.discard')}
            </button>
            <button type="button" className="xo-btn" disabled={busy || !dirty} onClick={save}>
              {busy ? tx('ui.products.settings.saving') : tx('ui.products.options.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
